// src/sync/supabase-fallback.js — Tier 2 Realtime Broadcast Channel (WebRTC Fallback)
// Provides instant ~25ms WebSocket broadcasting when carrier 5G NAT blocks WebRTC P2P.
import { validateMatchSnapshot, normalizeSnapshot } from '../match-schema.js';

const SUPABASE_PROJECT_URL = "https://fyopjvgqtrtwuwuteoev.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_ds2VJMM6V15Unn1_mKfwKA_N0TyH";

// Phoenix closes a socket that has not sent a heartbeat for 60s, and Android
// carrier NATs are usually more aggressive than that — ping well inside it.
const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 10_000;

let _fallbackSocket = null;
let _currentChannelCode = null;
let _onMessageCallback = null;
let _reconnectTimer = null;
let _heartbeatTimer = null;
let _backoff = 1000;
// State snapshots are idempotent: only the NEWEST one is worth sending, so a
// single slot is the correct queue. Dropping the payload outright (the old
// behaviour) lost every update sent during the handshake window.
let _pendingSnapshot = null;
let _channelReady = false;

function _clearTimers() {
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
}

function _rawSend(payload) {
  if (!_fallbackSocket || _fallbackSocket.readyState !== WebSocket.OPEN || !_currentChannelCode) return false;
  try {
    _fallbackSocket.send(JSON.stringify({
      topic: `realtime:match_${_currentChannelCode}`,
      event: "broadcast",
      payload: { type: "ball_update", data: payload, timestamp: Date.now() },
      ref: String(Date.now())
    }));
    return true;
  } catch (e) {
    console.warn(`[SupabaseFallback] Broadcast send failed:`, e);
    return false;
  }
}

function _flushQueue() {
  if (!_pendingSnapshot || !_channelReady) return;
  const payload = _pendingSnapshot;
  _pendingSnapshot = null;
  _rawSend(payload);
}

function _connectWs() {
  if (!_currentChannelCode) return;
  if (_fallbackSocket) {
    try { _fallbackSocket.onclose = null; _fallbackSocket.close(); } catch (_) {}
    _fallbackSocket = null;
  }
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }

  try {
    const wsUrl = `${SUPABASE_PROJECT_URL.replace('https://', 'wss://')}/realtime/v1/websocket?apikey=${SUPABASE_ANON_KEY}&vsn=1.0.0`;
    _fallbackSocket = new WebSocket(wsUrl);

    _fallbackSocket.onopen = () => {
      console.log(`[SupabaseFallback] Connected to Tier 2 WebSocket server.`);
      _backoff = 1000;
      _channelReady = false;
      try {
        _fallbackSocket.send(JSON.stringify({
          topic: `realtime:match_${_currentChannelCode}`,
          event: "phx_join",
          payload: {},
          ref: "1"
        }));
      } catch (_) {}
      _heartbeatTimer = setInterval(() => {
        if (_fallbackSocket?.readyState === WebSocket.OPEN) {
          try {
            _fallbackSocket.send(JSON.stringify({ topic: "phoenix", event: "heartbeat", payload: {}, ref: String(Date.now()) }));
          } catch (_) {}
        }
      }, HEARTBEAT_MS);
    };

    _fallbackSocket.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.event === "phx_reply" && msg.ref === "1") {
          _channelReady = true;
          _flushQueue();
        }
        if (msg.event === "broadcast" && msg.payload && _onMessageCallback) {
          const rawData = msg.payload.data;
          // Validate incoming fallback snapshot to prevent malicious injection
          if (!rawData || !rawData.matchId || !rawData.senderId) {
            console.warn("[SupabaseFallback] Missing matchId or senderId");
            return;
          }
          if (rawData && typeof rawData === 'object' && rawData.id) {
            const checked = validateMatchSnapshot(normalizeSnapshot(rawData));
            if (!checked.ok) {
              console.warn("[SupabaseFallback] Rejected invalid snapshot payload:", checked.error);
              return;
            }
            _onMessageCallback(checked.value);
          } else {
            _onMessageCallback(rawData); // simple events
          }
        }
      } catch (_) {}
    };

    _fallbackSocket.onerror = (err) => {
      console.warn(`[SupabaseFallback] WebSocket warning:`, err);
    };

    // Without this the channel never repaired itself after a brief network blip
    // and stayed permanently disconnected for the rest of the match.
    _fallbackSocket.onclose = () => {
      if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
      if (!_currentChannelCode) return; // intentional close
      if (_reconnectTimer) return;
      const delay = Math.min(_backoff, MAX_BACKOFF_MS);
      console.warn(`[SupabaseFallback] Socket closed — reconnecting in ${delay}ms`);
      _reconnectTimer = setTimeout(() => { _reconnectTimer = null; _connectWs(); }, delay);
      _backoff = Math.min(Math.round(_backoff * 1.5), MAX_BACKOFF_MS);
    };
  } catch (err) {
    console.warn(`[SupabaseFallback] Could not initialize fallback socket:`, err);
  }
}

/**
 * Initializes Supabase Broadcast Channel as fallback when WebRTC P2P drops.
 * @param {string} matchCode - 6-character match code
 * @param {function} onMessage - Callback when a ball update payload is received
 */
export function initSupabaseFallbackChannel(matchCode, onMessage) {
  if (!matchCode) return;
  closeSupabaseFallbackChannel();
  _currentChannelCode = String(matchCode).trim().toUpperCase();
  _onMessageCallback = onMessage;
  _backoff = 1000;
  console.log(`[SupabaseFallback] Initializing Tier 2 Broadcast Channel for match ${_currentChannelCode}`);
  _connectWs();
}

/**
 * Broadcasts a ball payload over Supabase Realtime Fallback.
 * Queues the newest payload while the socket is still handshaking.
 * @param {Object} payload - Ball event or match update payload
 */
export function broadcastBallViaSupabase(payload) {
  if (!_currentChannelCode || !payload) return false;
  if (!_fallbackSocket || _fallbackSocket.readyState !== WebSocket.OPEN) {
    _pendingSnapshot = payload; // keep only the newest — older states are worthless
    return false;
  }
  return _rawSend(payload);
}

/**
 * Closes the Supabase Broadcast Channel.
 */
export function closeSupabaseFallbackChannel() {
  // Null the channel code FIRST so onclose treats this as intentional and does
  // not schedule a reconnect. Clearing the callback also drops the retained
  // closure over the previous role's handler.
  _currentChannelCode = null;
  _onMessageCallback = null;
  _pendingSnapshot = null;
  _clearTimers();
  if (_fallbackSocket) {
    try { _fallbackSocket.onclose = null; _fallbackSocket.close(); } catch (_) {}
    _fallbackSocket = null;
  }
}
